import React, { useCallback, useContext, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useBlocker } from 'react-router-dom'
import {
  Divider,
  EnumHelperText,
  EnumSpinnerLoaderPosition,
  HelperText,
  SpinnerLoader,
  Status,
  Typography,
  Warning,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import {
  LINK_CAN_I_USE_INTERNATIONALLY_GUIDE,
  LINK_DOUBLE_OPT_IN_GUIDE,
  LINK_HELP_QUIET_HOURS,
  LINK_HIPAA_COMPLIANCE,
} from 'shared/constants/links'
import { makeTextFieldHighlightLimit, TextField } from 'shared/ui/TextField'
import { MAX_COMPANY_NAME_LENGTH } from 'shared/constants/auth'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { DoubleOptInSetting } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSetting/doubleOptInSetting'
import { DoubleOptInPageContext } from 'pages/settings/pages/doubleOptIn/context/doubleOptInContext'
import { DoubleOptInSettingAutoCloseAdditional } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSettingAutoCloseAdditional/doubleOptInSettingAutoCloseAdditional'
import { DoubleOptInSettingLayout } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSetting/DoubleOptInSettingLayout'
import { PurgePhiData } from 'pages/settings/pages/doubleOptIn/ui/PurgePhiData/PurgePhiData'
import { RequestAuditLogs } from 'pages/settings/pages/doubleOptIn/ui/RequestAuditLogs/RequestAuditLogs'
import { QuietHoursSettings } from 'pages/settings/pages/doubleOptIn/ui/QuietHoursSettings/QuietHoursSettings'
import { QuietHoursActions } from 'pages/settings/pages/doubleOptIn/ui/QuietHoursSettings/ui/QuietHoursActions/QuietHoursActions'
import styles from './styles.module.scss'

export const DoubleOptInGroups = observer(() => {
  const { featureFlagsStore } = useEntitiesContext()
  const store = useContext(DoubleOptInPageContext)

  const hasUnsavedQuietHours = store?.quietHoursStore?.isShowActions ?? false
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedQuietHours && currentLocation.pathname !== nextLocation.pathname
  )

  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = true
  }, [])

  useEffect(() => {
    if (blocker.state === 'blocked' && store?.quietHoursStore) {
      store.quietHoursStore.openAskIfSaveModal({
        blocker,
        onToggle: () => uiStore.setRoutesBlocker(null),
      })
    }
  }, [blocker.state, store?.quietHoursStore])

  useEffect(() => {
    uiStore.setRoutesBlocker(hasUnsavedQuietHours ? blocker : null)
    if (hasUnsavedQuietHours) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler)
  }, [hasUnsavedQuietHours, blocker, beforeUnloadHandler])

  if (!store || !store.isStoreInitialized)
    return (
      <div className={styles.wrap}>
        <SpinnerLoader
          ariaLabel={'doubleOptInGroups'}
          position={EnumSpinnerLoaderPosition.Absolute}
        />
      </div>
    )

  const {
    categories,
    selectedCategory,
    handleCategorySwitch,
    isSelectedCategoryGeneral,
    isSelectedCategoryConversations,
    isSelectedCategoryCalling,
    isSelectedCategorySecurity,
    companyName,
    setCompanyName,
    updateCompanyName,
    limitReached,
    setLimitReached,
  } = store

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <div className={styles.categories}>
          {categories.map((category) => (
            <Status
              className={styles.categoryItem}
              key={category.key}
              emphasis='high'
              size='medium'
              intent='noBorder'
              title={category.label}
              onClick={() => handleCategorySwitch(category.key)}
              isActive={selectedCategory === category.key}
            />
          ))}
        </div>
      </div>
      {isSelectedCategoryGeneral && (
        <div className={styles.content} aria-label='General'>
          <Typography
            variant={'heading-md'}
            ariaLabel={'General Title'}
            color={'var(--content-primary-primary)'}
            className={styles.categoryTitle}
          >
            General
          </Typography>

          <Typography
            ariaLabel='Company name'
            variant='text-input-label-small'
            color='var(--content-primary-tertiary)'
            marginBottom={'8.5px'}
          >
            Company name
          </Typography>
          <div className={styles.inputWrap}>
            <TextField
              variant='stroke'
              size='medium'
              value={companyName}
              error={!companyName}
              onBlur={updateCompanyName}
              onChange={setCompanyName}
              mainActions={[
                makeTextFieldHighlightLimit({
                  limit: MAX_COMPANY_NAME_LENGTH,
                  onChange: setLimitReached,
                }),
              ]}
            />
            <div style={{ paddingTop: '4px' }}>
              {!companyName && (
                <HelperText
                  icon={'alertTriangleSmall'}
                  variant={EnumHelperText.Error}
                  text={'Please enter a company name'}
                />
              )}
              {limitReached && (
                <Warning text={`${MAX_COMPANY_NAME_LENGTH}-character limit reached`} />
              )}
            </div>
          </div>
          <DoubleOptInSetting
            title={'Quiet hours'}
            titleVariant={'body-lg-medium'}
            titleRightContent={
              <Status title={'Beta'} intent={'warning'} emphasis={'high'} size={'label'} />
            }
            description={
              'Delay sending messages and making calls between specific hours to avoid contacting recipients at inappropriate times.'
            }
            learnMoreLink={LINK_HELP_QUIET_HOURS}
            option={'quietHours'}
            disabled={store.quietHoursStore?.loading}
          />
          <QuietHoursSettings />
          <QuietHoursActions />
        </div>
      )}
      {isSelectedCategoryConversations && (
        <div className={styles.content} aria-label='Conversations'>
          <Typography
            variant={'heading-md'}
            ariaLabel={'Conversations Title'}
            color={'var(--content-primary-primary)'}
            className={styles.categoryTitle}
          >
            Conversations
          </Typography>
          <DoubleOptInSetting
            title={'Auto-close conversations'}
            description={(checked) =>
              checked
                ? 'Automatically close inactive conversations after'
                : 'Automatically close inactive conversations'
            }
            option={'autoCloseConversation'}
            className={styles.settingsItem}
            renderContent={(checked) =>
              checked ? <DoubleOptInSettingAutoCloseAdditional /> : null
            }
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Auto-close unsubscribe'}
            description={'Automatically close conversations when contacts unsubscribe'}
            option={'autoCloseUnsubscribe'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Auto-assign contact owner'}
            description={
              'The first team member to text or call a contact becomes the contact’s owner'
            }
            option={'autoAssignContactOwner'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Global conversations history'}
            description={
              'Show conversations from all inboxes in the conversations history timeline'
            }
            option={'globalConversationHistory'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Double Opt-in'}
            description={
              'Send messages only after contacts confirm their number with “Y” or “Yes”.'
            }
            learnMoreLink={LINK_DOUBLE_OPT_IN_GUIDE}
            option={'doubleOptIn'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Extended international texting'}
            description={
              'Control texting to international numbers. Disable to send messages to US, CA, GB or AU only.'
            }
            learnMoreLink={LINK_CAN_I_USE_INTERNATIONALLY_GUIDE}
            option={'extendedInternationalTexting'}
            className={styles.settingsItem}
          />
        </div>
      )}
      {isSelectedCategoryCalling && (
        <div className={styles.content} aria-label='Calling'>
          <Typography
            variant={'heading-md'}
            ariaLabel={'Calling Title'}
            color={'var(--content-primary-primary)'}
            className={styles.categoryTitle}
          >
            Calling
          </Typography>
          <DoubleOptInSetting
            title={'Outbound calls'}
            description={'Enable outbound calls for your workspace'}
            option={'outboundCalls'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Inbound calls'}
            description={'Enable inbound calls for your workspace'}
            option={'inboundCalls'}
            className={styles.settingsItem}
          />
          <Divider
            style={{
              width: '100%',
              marginTop: '7.5px',
              marginBottom: '7.5px',
            }}
          />
          <DoubleOptInSetting
            title={'Record automatically'}
            description={'Force all calls to be recorded'}
            option={'recordAutomatically'}
            className={styles.settingsItem}
          />
        </div>
      )}
      {isSelectedCategorySecurity && (
        <div className={styles.content} aria-label='Security'>
          <Typography
            variant={'heading-md'}
            ariaLabel={'Security Title'}
            color={'var(--content-primary-primary)'}
            className={styles.categoryTitle}
          >
            Security
          </Typography>
          <DoubleOptInSetting
            title={'Require two-step authentication for your team'}
            description={'All your team members will be required to use two factor authentication'}
            option={'requireTwoStepAuth'}
            disabled={store.disabledTwoStepAuth}
            className={styles.settingsItem}
          />

          {featureFlagsStore.hipaa_setting_toggle && store.isHipaaSettingVisible && (
            <>
              <Divider
                style={{
                  width: '100%',
                  marginTop: '7.5px',
                  marginBottom: '7.5px',
                }}
              />
              <DoubleOptInSetting
                title={'HIPAA Compliance status'}
                titleRightContent={
                  <Status title={'Beta'} intent={'warning'} emphasis={'high'} size={'label'} />
                }
                description={
                  'Implements stricter security with timeouts, logging, and 2FA for HIPAA compliance.'
                }
                learnMoreLink={LINK_HIPAA_COMPLIANCE}
                option={'hipaaCompliance'}
                className={styles.settingsItem}
              />
            </>
          )}

          {featureFlagsStore.hipaa_setting_toggle &&
            store.isHipaaSettingVisible &&
            store.isHipaaCompliant && (
              <>
                <Divider
                  style={{
                    width: '100%',
                    marginTop: '7.5px',
                    marginBottom: '7.5px',
                  }}
                />
                <DoubleOptInSettingLayout
                  title={'Purge PHI data'}
                  description={
                    'This will permanently archive your PHI data. This action can’t be undone.'
                  }
                  variant={'content'}
                  rightContent={<PurgePhiData />}
                />
              </>
            )}

          {featureFlagsStore.hipaa_setting_toggle &&
            store.isHipaaSettingVisible &&
            store.isHipaaCompliant && (
              <>
                <Divider
                  style={{
                    width: '100%',
                    marginTop: '7.5px',
                    marginBottom: '7.5px',
                  }}
                />
                <DoubleOptInSettingLayout
                  title={'Request audit logs'}
                  description={
                    'Export your organization’s audit activity for compliance and monitoring.'
                  }
                  variant={'content'}
                  rightContent={<RequestAuditLogs />}
                />
              </>
            )}
        </div>
      )}
    </div>
  )
})
